import type { ReactNode } from 'react';

import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { SheetTitle } from '@/shared/components/ui/sheet';
import { cn } from '@/shared/lib/utils';

/**
 * 거래처 드로어 공통 머리 (Figma 드로어 t 840:5629 · 842:7413) — 흰 바탕 · 위 40.
 * 제목 20 Bold (+ 옆 보조 문구 13 Regular) · ✕(40) / 병원명 · 종류 줄 16 / 아래 탭은 children
 */
export const DrawerHeader = ({
  title,
  subtitle,
  clinicName,
  clinicTypeLabel,
  onClose,
  children,
}: {
  title: string;
  /** 제목 옆 작은 문구 (예: "26.07 확정 처방 기준") */
  subtitle?: string | null;
  clinicName: string;
  clinicTypeLabel: string;
  onClose: () => void;
  /** 탭 줄 */
  children?: ReactNode;
}) => (
  <header className="flex shrink-0 flex-col gap-6 bg-surface-white pt-10">
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-6 px-6">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2">
          <SheetTitle className="text-heading-20-b text-fg-primary">
            {title}
          </SheetTitle>
          {subtitle && (
            <span className="text-body-13-r text-fg-tertiary">{subtitle}</span>
          )}
        </div>
        <Button
          variant="text"
          onClick={onClose}
          aria-label="닫기"
          leftIcon={<Icon name="cancel-line-24" />}
        />
      </div>
      <dl className="flex flex-col gap-2 px-6 text-body-16-m">
        <InfoRow label="병원명">{clinicName}</InfoRow>
        <InfoRow label="종류">{clinicTypeLabel}</InfoRow>
      </dl>
    </div>
    {children}
  </header>
);

const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex items-center gap-2">
    <dt className="min-w-[42px] text-fg-tertiary">{label}</dt>
    <dd className="text-body-16-sb text-fg-primary">{children}</dd>
  </div>
);

/** 드로어 본문 카드 (Figma list 840:6582) — 흰 바탕 r20 · 안쪽 24, 행 사이 16 + 1px 선 */
export const DrawerListCard = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <ul
    className={cn(
      'flex flex-col rounded-20 bg-surface-white p-6 [&>li+li]:mt-4 [&>li+li]:border-t [&>li+li]:border-line-grey [&>li+li]:pt-4',
      className,
    )}
  >
    {children}
  </ul>
);

/** 탭 이름 + 파란 숫자 (Figma Tab (Item) "거래중 11") */
export const TabCount = ({
  label,
  count,
}: {
  label: string;
  count: number;
}) => (
  <>
    {label} <span className="text-fg-accent">{count}</span>
  </>
);
